import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { find, findIndex, map, size } from 'es-toolkit/compat';
import React from 'react';

import SlideThumbnailImage from 'components/StudyDashboard/ProceduresPage/ProcedureCard/Carousel/SlideThumbnailImage';
import { Slide } from 'interfaces/slide';
import { getStainStrWithNegPosControl } from 'utils/helpers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

const dialogThumbnailSx = {
  position: 'relative',
  width: 68,
  height: 68,
  overflow: 'hidden',
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
  flexShrink: 0,
} as const;

interface SlidePreviewPanelProps {
  slides?: Slide[];
  selectedSlideId?: string | null;
  onSelectSlide: (slideId: string) => void;
  detailsContent?: React.ReactNode;
  previewMinHeight?: number;
}

export const SlidePreviewPanel: React.FC<SlidePreviewPanelProps> = ({
  slides,
  selectedSlideId,
  onSelectSlide,
  detailsContent,
  previewMinHeight = 360,
}) => {
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const selectedSlide = find(slides, ({ id }) => id === selectedSlideId) ?? slides?.[0];
  const indexOfSelectedSlide = findIndex(slides, ({ id }) => id === selectedSlideId);

  if (!selectedSlide) {
    return (
      <Typography variant="body2" color="text.secondary">
        Preview not available.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: previewMinHeight,
          maxHeight: 'min(42vh, 420px)',
          overflow: 'hidden',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default',
        }}
      >
        <SlideThumbnailImage slide={selectedSlide} />
      </Box>

      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {selectedSlide.id}
        </Typography>
        {selectedSlide.stainingType && (
          <Typography variant="body2" color="text.secondary">
            {getStainStrWithNegPosControl(
              stainTypeIdToDisplayName(selectedSlide.stainingType),
              selectedSlide.negativeControl,
              selectedSlide.positiveControl
            )}
          </Typography>
        )}
        {selectedSlide.originalFileName && (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {selectedSlide.originalFileName}
          </Typography>
        )}
        {detailsContent}
      </Stack>

      {size(slides) > 1 && (
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {map(slides, (slide, index) => {
            const isSelected = slide.id === selectedSlide.id;

            return (
              <Tooltip
                key={slide.id}
                title={`Preview ${size(slides) === 1 ? slide.id : `${index + 1} of ${size(slides)}`}`}
              >
                <ButtonBase
                  onClick={() => onSelectSlide(slide.id)}
                  aria-label={`Select slide ${slide.id}`}
                  sx={{ borderRadius: 1.5 }}
                >
                  <Box
                    sx={{
                      ...dialogThumbnailSx,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      boxShadow: isSelected ? 2 : 0,
                    }}
                  >
                    <SlideThumbnailImage slide={slide} />
                  </Box>
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
